import { useRef, useImperativeHandle, forwardRef } from 'react';
import { PayfortFormHandle, SubmitArgs } from '~/components/UserCardForm/types';
import { PaymentMethodEnum } from '~/constants/enums/paymentEnums';
import { usePostPaymentPayMutation } from '~/redux/api/PaymentApiSlice';

type PayfortFormProps = {
  id?: string;
  onFailure?: () => void;
};

const PayfortForm = forwardRef<PayfortFormHandle, PayfortFormProps>(
  ({ id = 'payfort-form', onFailure }, ref) => {
    const formRef = useRef<HTMLFormElement | null>(null);
    const [postPaymentPay] = usePostPaymentPayMutation();

    const handleSubmitPayfortForm = (
      inputs: SubmitArgs['inputs'],
      action: string,
    ) => {
      // append new hidden inputs
      if (formRef.current) {
        for (const [key, value] of Object.entries(inputs)) {
          // check if input exists
          let input = formRef?.current?.querySelector<HTMLInputElement>(
            `input[name="${key}"]`,
          );
          if (input) {
            input.value = value.toString();
          } else {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value.toString();
            formRef.current?.appendChild(input);
          }
        }
        formRef.current.action = action;
        formRef.current.submit();
      }
    };

    useImperativeHandle(ref, () => ({
      submitPayfort: async (args: SubmitArgs) => {
        try {
          const { inputs, params } = args;

          // Draft the new-card transaction via the unified /pay endpoint
          // (NEW_CARD), then post the card form to Payfort using the returned
          // tokenization params. Covers payments and SAVE_CARD alike.
          const result = await postPaymentPay({
            ...params,
            paymentMethod: PaymentMethodEnum.NEW_CARD,
          }).unwrap();
          if (result?.status === 'tokenization_required' && result.params) {
            handleSubmitPayfortForm(
              { ...inputs, ...result.params.params },
              result.params.url,
            );
          } else if (onFailure) {
            onFailure();
          }
        } catch (error) {
          if (onFailure) onFailure();
          console.error('Error submitting Payfort form:', error);
        }
      },
    }));

    return <form id={id} method="POST" ref={formRef}></form>;
  },
);

export default PayfortForm;
